import { useCallback, useMemo } from 'react'
import { useUpdaterChainId } from '~/hooks'
import { useLimitSwapPair, useLimitSwapAmount } from '~/state/dex/hooks'
import { useFeeData } from '~/state/application/hooks'
import { useTokenPrice } from '~/hooks/useTokenPrice'
import BigNumber from 'bignumber.js'
import { AddressZero } from '@ethersproject/constants'
import { useLimitOrderMarketRate } from './useLimitOrderPrices'

const DEFAULT_GAS_LIMIT = {
  1: '300000',
  11155111: '300000',
  42161: '2000000',
  421611: '2000000',
}

export const useEstimatedExecutionPrice = (
  inputToken: ERC20Token,
  inputAmount: string,
  outputAmount: string,
) => {
  const chainId = useUpdaterChainId()
  const inputPrice = useTokenPrice(inputToken?.address)
  const ethPrice = useTokenPrice(AddressZero)
  const feeData = useFeeData()

  return useCallback(() => {
    const gasPrice = feeData?.gasPrice.toString() || '0'
    const gasLimit = DEFAULT_GAS_LIMIT[chainId]
    const gasFee = new BigNumber(gasLimit).multipliedBy(
      new BigNumber(gasPrice).dividedBy(1e18),
    )
    const gasFeeUSD = gasFee.multipliedBy(ethPrice)
    const feeAmount = gasFeeUSD.dividedBy(inputPrice)
    const price = new BigNumber(outputAmount)
      .dividedBy(new BigNumber(inputAmount).minus(feeAmount))
      .sd(4)
      .toString()
    return Number(price) ? price : '~'
  }, [inputAmount, outputAmount, ethPrice, inputPrice, feeData, chainId])
}

export const useEstimatedFastSellPrice = () => {
  const chainId = useUpdaterChainId()
  const [inputToken, outputToken] = useLimitSwapPair()
  const { inputAmount, outputAmount } = useLimitSwapAmount()
  const inputPrice = useTokenPrice(inputToken?.address)
  const outputPrice = useTokenPrice(outputToken?.address)
  const ethPrice = useTokenPrice(AddressZero)
  const feeData = useFeeData()
  const { marketRate } = useLimitOrderMarketRate(inputToken, outputToken)

  return useMemo(() => {
    const gasPrice = feeData?.gasPrice.toString() || '0'
    const gasLimit = DEFAULT_GAS_LIMIT[chainId]
    const gasFee = new BigNumber(gasLimit).multipliedBy(
      new BigNumber(gasPrice).dividedBy(1e18),
    )
    const gasFeeUSD = gasFee.multipliedBy(ethPrice)
    const feeAmount = gasFeeUSD.dividedBy(inputPrice)

    const marketOutputAmount = new BigNumber(marketRate).multipliedBy(
      new BigNumber(inputAmount).minus(feeAmount),
    )

    const price = marketOutputAmount.dividedBy(inputAmount).toFixed(6)
    return Number(price) ? price : '0'
  }, [
    inputAmount,
    outputAmount,
    ethPrice,
    inputPrice,
    outputPrice,
    feeData?.gasPrice,
    chainId,
  ])
}
